import { KeyboardEvent, useState, useMemo, useCallback } from 'react';

import { slashCommandShortcuts, SlashCommandShortcut } from '../AssistantChatInput/constants';

interface UseSlashCommandProps {
  message: string;
  onSelect: (shortcut: SlashCommandShortcut) => void;
  onClearMessage: () => void;
}

export const useSlashCommand = ({ message, onSelect, onClearMessage }: UseSlashCommandProps) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  const isSlashActive = useMemo(() => message.startsWith('/'), [message]);

  const filteredShortcuts = useMemo(() => {
    if (!isSlashActive) {
      return [];
    }
    const query = message.toLowerCase();
    if (query === '/') {
      return slashCommandShortcuts;
    }
    return slashCommandShortcuts.filter((shortcut) => shortcut.command.includes(query));
  }, [message, isSlashActive]);

  const showMenu = isSlashActive && filteredShortcuts.length > 0;

  const resetSelection = useCallback(() => {
    setSelectedIndex(0);
  }, []);

  const selectShortcut = useCallback(
    (shortcut: SlashCommandShortcut) => {
      onClearMessage();
      resetSelection();
      onSelect(shortcut);
    },
    [onSelect, onClearMessage, resetSelection]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>): boolean => {
      if (!showMenu) {
        return false;
      }

      switch (event.key) {
        case 'ArrowUp':
          event.preventDefault();
          setSelectedIndex((prev) => (prev <= 0 ? filteredShortcuts.length - 1 : prev - 1));
          return true;
        case 'ArrowDown':
          event.preventDefault();
          setSelectedIndex((prev) => (prev >= filteredShortcuts.length - 1 ? 0 : prev + 1));
          return true;
        case 'Enter':
          if (event.shiftKey) return false;
          event.preventDefault();
          if (filteredShortcuts[selectedIndex]) {
            selectShortcut(filteredShortcuts[selectedIndex]);
          }
          return true;
        case 'Tab':
          event.preventDefault();
          if (filteredShortcuts[selectedIndex]) {
            selectShortcut(filteredShortcuts[selectedIndex]);
          }
          return true;
        case 'Escape':
          event.preventDefault();
          onClearMessage();
          resetSelection();
          return true;
        default:
          return false;
      }
    },
    [showMenu, filteredShortcuts, selectedIndex, selectShortcut, onClearMessage, resetSelection]
  );

  return {
    showMenu,
    filteredShortcuts,
    selectedIndex,
    selectShortcut,
    resetSelection,
    handleKeyDown,
  };
};
